import api from './api';

// Types
export interface Category {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  createdAt: string;
}

export interface Sector {
  id: string;
  name: string;
  description: string | null;
  regulator: string | null;
  createdAt: string;
}

export interface Product {
  id: string;
  title: string;
  description: string;
  price: string;
  category: string;
  sector: string;
  tags: string[];
  status: string;
  downloads: number;
  rating: number;
  reviewCount: number;
  seller: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryDto {
  name: string;
  description?: string;
  icon?: string;
}

export interface UpdateCategoryDto {
  name?: string;
  description?: string;
  icon?: string;
}

export interface CreateSectorDto {
  name: string;
  description?: string;
  regulator?: string;
}

export interface UpdateSectorDto {
  name?: string;
  description?: string;
  regulator?: string;
}

// Categories Service
export const categoriesService = {
  getAll: async (): Promise<{ categories: Category[]; total: number }> => {
    const response = await api.get('/categories');
    return response.data;
  },

  getById: async (id: string): Promise<Category> => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },

  create: async (data: CreateCategoryDto): Promise<Category> => {
    const response = await api.post('/categories', data);
    return response.data;
  },

  update: async (id: string, data: UpdateCategoryDto): Promise<Category> => {
    const response = await api.patch(`/categories/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

// Sectors Service
export const sectorsService = {
  getAll: async (): Promise<{ sectors: Sector[]; total: number }> => {
    const response = await api.get('/sectors');
    return response.data;
  },

  getById: async (id: string): Promise<Sector> => {
    const response = await api.get(`/sectors/${id}`);
    return response.data;
  },

  create: async (data: CreateSectorDto): Promise<Sector> => {
    const response = await api.post('/sectors', data);
    return response.data;
  },

  update: async (id: string, data: UpdateSectorDto): Promise<Sector> => {
    const response = await api.patch(`/sectors/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/sectors/${id}`);
    return response.data;
  },
};

// Products Service
export const productsService = {
  getAll: async (filters?: {
    status?: string;
    category?: string;
    sector?: string;
    search?: string;
  }): Promise<Product[]> => {
    const params: Record<string, any> = {};
    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key as keyof typeof filters];
        if (value !== undefined && value !== null) {
          params[key] = value;
        }
      });
    }
    
    const response = await api.get('/products', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Product> => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  update: async (id: string, data: { status?: string }): Promise<Product> => {
    const response = await api.patch(`/products/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/products/${id}`);
  },
};

